import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ValueChangedEvent } from 'devextreme/ui/check_box';
import { ParameterDefinition, ParameterType } from '../../../core/src/lib/models/parameter-definition.model';
import { ParameterValue } from '../../../core/src/lib/models/parameter-value.model';
import { ParameterService } from './services/parameter.service';
import { ColumnInfo } from '@barthauer/abc/core/data';

@Component({
    selector: 'param-values-editor',
    templateUrl: './values-editor.component.html',
    styleUrls: ['./values-editor.component.scss'],
    standalone: false
})
export class ValuesEditorComponent implements OnInit {
  @Input() parameter: any;
  @Output() parameterValueChange = new EventEmitter<ParameterValue>();

  private parameterDefinitionUrl: string;
  public valueExpression = '';
  public picklistByColumnData: object[];
  public picklistByColumnValuesList: string[] = [];
  public picklistByNameData: object[];
  public picklistByNameValuesList: string[] = [];
  public objectSelectionData: object[];
  public objectSelectionIds: string[] = [];
  public objectSelectionColumnName: string | undefined;
  public objectSelectionColumnInfos: ColumnInfo[] = [];
  public objectSelectionColumns: object[] = [];
  public valueMultipleSelectionData: object[];
  public valueSingleSelectionData: string[];
  public valueSelectionItems: string[] = [];
  public valueSelectionColumnName: string | undefined;
  public queryData: object[];
  public queryValuesList: string[] = [];
  public queryColumns: object[] = [];

  public isPicklistByNameScrolling = false;
  public isPicklistByColumnScrolling = false;
  public isDataAccessByValueScrolling = false;
  public isDataAccessByObjectTypeScrolling = false;
  public isQueryScrolling = false;

  public res = ResourceSet.Empty;
  public resAsync: Promise<ResourceSet>;

  constructor(
    private configService: AbcConfigService,
    private parameterService: ParameterService,
    private resourceService: ResourceManagerService
  ) {
    this.parameterDefinitionUrl = this.configService.configuration.WebApi.ServiceURLs.DefaultParameterDefinitions;
    this.resAsync = this.resourceService.getResourceSet('abc-parameters-values-editor');
  }

  async ngOnInit() {
    this.res = await this.resAsync;

    switch (this.parameter.Type) {
      case ParameterType.PicklistByName:
        this.getPicklistByNameData(this.parameter.DataArgument, this.parameter.DataArgument2);
        break;
      case ParameterType.PicklistByColumn:
        this.getPicklistByColumnData(this.parameter.DataArgument, this.parameter.DataArgument2);
        break;
      case ParameterType.DataAccessByValue:
        this.getValueSelectionData(this.parameter.DataArgument, this.parameter.DataArgument2);
        break;
      case ParameterType.DataAccessByObjectType:
        this.objectSelectionDataEx(this.parameter);
        break;
      case ParameterType.Query:
        this.onQueryData(this.parameter.DataQuery, this.parameter.DataArgument2);
        break;
      default:
    }
  }

  onTextValueChange(e: object, parameterName: string, parameterValue: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: parameterValue });
  }

  onCheckboxValueChange(e: ValueChangedEvent, parameterName: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: e.value });
  }

  onDateValueChange(e: object, parameterName: string, parameterValue: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: parameterValue });
  }

  onNumberValueChange(e: object, parameterName: string, parameterValue: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: parameterValue });
  }

  onValueExpressionChange(e: object, parameterName: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: this.valueExpression });
  }

  getPicklistByNameData(picklistname: string, language: number) {
    this.parameterService.queryPicklistItems(picklistname, language).subscribe((result: any) => {
      this.picklistByNameData = result.Data;
      if (this.picklistByNameData.length > 12) {
        this.isPicklistByNameScrolling = true;
      }
    });
  }

  onPicklistByNameMultipleSelectionChange(e: any, parameterName: string) {
    this.picklistByNameValuesList = e.selectedRowsData.map((obj: any) => obj.Value);
    this.parameterValueChange.emit({ Name: parameterName, Value: this.picklistByNameValuesList });
  }

  onPicklistByNameSingleSelectionChange(e: any, parameterName: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: e.value });
  }

  getPicklistByColumnData(objectType: string, columnName: string) {
    this.parameterService
      .queryPicklistItemsByObjectTypeAndColumnName(objectType, columnName)
      .subscribe((result: any) => {
        this.picklistByColumnData = result.Data;
        if (this.picklistByColumnData.length > 12) {
          this.isPicklistByColumnScrolling = true;
        }
      });
  }

  onPicklistByColumnMultipleSelectionChange(e: any, parameterName: string) {
    this.picklistByColumnValuesList = e.selectedRowsData.map((obj: any) => obj.Value);
    this.parameterValueChange.emit({ Name: parameterName, Value: this.picklistByColumnValuesList });
  }

  onPicklistByColumnSingleSelectionChange(e: any, parameterName: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: e.value });
  }

  getValueSelectionData(objectType: string, columnName: string) {
    this.parameterService
      .queryTargetColumnValuesByObjectTypeAndColumn(objectType, columnName)
      .subscribe((result: any) => {
        this.valueMultipleSelectionData = result;
        let valuesList = result.map((object: any) => Object.values(object)[0]);
        this.valueSingleSelectionData = valuesList;
        this.valueSelectionColumnName = Object.keys(result[0])[0];
        if (this.valueMultipleSelectionData.length > 12) {
          this.isDataAccessByValueScrolling = true;
        }
      });
  }

  onValueMultipleSelectionChange(e: any, parameterName: string) {
    this.valueSelectionItems = e.selectedRowsData.map((obj: any) => obj[<string>this.valueSelectionColumnName]);
    this.parameterValueChange.emit({ Name: parameterName, Value: this.valueSelectionItems });
  }

  onValueSingleSelectionChange(e: any, parameterName: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: e.selectedItem });
  }

  objectSelectionDataEx(para: ParameterDefinition) {
    const objType = para.DataArgument;
    const filter = para.DataArgument2;
    this.parameterService.queryObjectDataSourceByObjectTypeEx(objType, filter).subscribe((result: any) => {
      this.objectSelectionData = result.Data;
      this.objectSelectionColumnInfos = result.ColumnInfos;
      this.objectSelectionColumnName = Object.keys(this.objectSelectionData[0]).find(
        (columnName: any) => columnName !== 'Id' && columnName !== 'ObjectType'
      );

      if (this.objectSelectionColumnInfos?.length) {
        this.objectSelectionColumns = this.objectSelectionColumnInfos
          .map((columnInfo) => ({
            dataField: columnInfo?.Name ?? '',
            caption: columnInfo?.Caption ?? '',
          }))
          .filter((c) => c.dataField !== 'Id' && c.dataField !== 'ObjectType');
      } else {
        this.objectSelectionColumns = [];
      }

      if (this.objectSelectionData.length > 12) {
        this.isDataAccessByObjectTypeScrolling = true;
      }
    });
  }

  objectMultipleSelectionChange(e: any, parameterName: string) {
    this.objectSelectionIds = e.selectedRowsData.map((obj: any) => obj.Id);
    this.parameterValueChange.emit({ Name: parameterName, Value: this.objectSelectionIds });
  }

  objectSingleSelectionChange(e: any, parameterName: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: e.newValue ?? e.value });
  }

  onQueryData(dataQuery: any, firstColumnName: string) {
    this.parameterService.queryDataFromQueryDefinitionAsArray(dataQuery).subscribe((result: any) => {
      const dataList = result[Object.keys(result)[0]];
      this.queryData = dataList;
      const queryColumnsName = Object.keys(dataList[0]);
      const position = queryColumnsName.indexOf(firstColumnName);
      queryColumnsName.splice(position, 1);
      queryColumnsName.unshift(firstColumnName);
      this.queryColumns = queryColumnsName.map((column: any) => ({ dataField: column, caption: column }));
      if (this.queryData.length > 12) {
        this.isQueryScrolling = true;
      }
    });
  }

  queryMultipleSelectionChange(e: any, parameterName: string, columnName: string) {
    this.queryValuesList = e.selectedRowsData.map((obj: any) => obj[columnName]);
    this.parameterValueChange.emit({ Name: parameterName, Value: this.queryValuesList });
  }

  querySingleSelectionChange(e: any, parameterName: string) {
    this.parameterValueChange.emit({ Name: parameterName, Value: e.newValue ?? e.value });
  }
}
